import {Alert, Button, Col, Container, FloatingLabel, Form, Modal, Row} from "react-bootstrap";
import React, {Fragment, useContext, useEffect, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {UsacmContext} from "../App";
import {
  deleteCourseDocument, getCourseDocumentList, updateCourseDocument, uploadCourseDocument
} from "../api/CourseApi";
import {toolTipRenderer} from "../utils/gridUtils";
import {dateTimeToStr, formatFileSize, getConfLabel, showSuccessToast, titleCase} from "../utils/usacmUtils";
import {fieldHasErrors, getErrorMessageForField} from "../utils/formUtils";
import {CONF_LABEL_KEY_COURSE, CONF_LABEL_KEY_COURSE_PAGE, COURSE_DOCUMENT_MAX_BYTES} from "../constants";

/**
 * The documents in one course, and the dialog to upload a new one or edit one.  The files are stored in the private
 * file upload bucket - see courses_api.course_document_upload().
 * Each document comes with used_on_pages - the titles of the pages whose html has its {doc:key}.  Deleting the
 * document or changing its key leaves that {doc:key} as plain text on those pages, so both warn first (but still
 * allow it - the user may be about to re-upload the document with the same key).
 */
export function CourseDocuments({course, canEdit, onClose}) {
  const context = useContext(UsacmContext);
  const [conf,] = context.conference;
  const [documentList, setDocumentList] = useState([]);
  const [errors, setErrors] = useState([]);
  // The document in the add/edit dialog - {} while adding a new one, null when the dialog is closed
  const [editingDocument, setEditingDocument] = useState(null);
  const [deletingDocument, setDeletingDocument] = useState(null);
  // Add/edit dialog fields
  const [editTitle, setEditTitle] = useState('');
  const [editDocKey, setEditDocKey] = useState('');
  const [editFile, setEditFile] = useState(null);
  const [editErrors, setEditErrors] = useState([]);
  const gridRef = useRef();
  const hasFewRows = (documentList || []).length < 12;
  const courseLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE));
  const pageLabel = getConfLabel(conf, CONF_LABEL_KEY_COURSE_PAGE).toLowerCase();
  const maxMb = COURSE_DOCUMENT_MAX_BYTES / (1024 * 1024);
  const isAdding = !!editingDocument && !editingDocument.id;
  const editKeyChanged = !!editingDocument && !isAdding && editDocKey.trim() !== editingDocument.doc_key;

  function loadDocuments() {
    if (!course?.id) {
      return;
    }
    getCourseDocumentList(course.id, (code, data, newErrors) => {
      if (code === 200) {
        setDocumentList(data);
        setErrors([]);
      } else {
        setErrors(newErrors);
      }
    });
  }

  useEffect(() => {
    loadDocuments();
  }, [course?.id]);

  /**
   * Opens the edit or delete dialog.  The list is reloaded first so the pages using the key are current - they
   * may have been changed on the Pages tab since this list was loaded.
   */
  function startDocumentAction(courseDocument, onFreshDocument) {
    getCourseDocumentList(course.id, (code, data, newErrors) => {
      if (code === 200) {
        setDocumentList(data);
        setErrors([]);
        const freshDocument = data.find(doc => doc.id === courseDocument.id);
        if (freshDocument) {
          onFreshDocument(freshDocument);
        } else {
          setErrors([{message: 'That document no longer exists.', fields: ['']}]);
        }
      } else {
        setErrors(newErrors);
      }
    });
  }

  /**
   * Opens the add/edit dialog - pass {} to add a new document
   */
  function openDocumentDialog(courseDocument) {
    setEditTitle(courseDocument.title || '');
    setEditDocKey(courseDocument.doc_key || '');
    setEditFile(null);
    setEditErrors([]);
    setEditingDocument(courseDocument);
  }

  function startEditingDocument(courseDocument) {
    startDocumentAction(courseDocument, openDocumentDialog);
  }

  function callSaveDocument() {
    // nginx rejects big uploads before they reach the server, so catch it here to give a useful message
    if (editFile && editFile.size > COURSE_DOCUMENT_MAX_BYTES) {
      setEditErrors([{message: `The file must be less than ${maxMb}MB.`, fields: ['file']}]);
      return;
    }
    const onComplete = (code, data, newErrors) => {
      if (code === 200) {
        showSuccessToast(isAdding ? 'Uploaded document.' : 'Updated document.');
        setDocumentList(data);
        setEditingDocument(null);
        setEditErrors([]);
      } else {
        setEditErrors(newErrors);
      }
    };
    if (isAdding) {
      uploadCourseDocument(course.id, editTitle, editDocKey, editFile, onComplete);
    } else {
      updateCourseDocument(editingDocument.id, editTitle, editDocKey, editFile, onComplete);
    }
  }

  function callDeleteDocument() {
    deleteCourseDocument(deletingDocument?.id, (code, data, newErrors) => {
      setDeletingDocument(null);
      if (code === 200) {
        showSuccessToast('Deleted document.');
        setDocumentList(data);
        setErrors([]);
      } else {
        setErrors(newErrors);
      }
    });
  }

  /**
   * The warning shown when a document's {doc:key} is about to stop working on the pages which use it
   */
  function renderKeyInUseWarning(courseDocument, whatHappens) {
    const pages = courseDocument?.used_on_pages || [];
    if (pages.length === 0) {
      return null;
    }
    return (
      <Alert variant="warning" className="mb-0">
        The key <code>{'{doc:' + courseDocument.doc_key + '}'}</code> is used
        on {pages.length === 1 ? `this ${pageLabel}` : `these ${pageLabel}s`}:
        <ul className="mb-2">
          {pages.map((pageTitle, index) => <li key={index}>{pageTitle}</li>)}
        </ul>
        {whatHappens} the <code>{'{doc:' + courseDocument.doc_key + '}'}</code> will show as plain text there instead of
        a link, until a document with that key is added again.
      </Alert>
    );
  }

  // Column setup
  const colDefs = [
    {field: 'title', cellRenderer: toolTipRenderer, headerName: 'Title', flex: 3, minWidth: 150},
    {field: 'doc_key', cellRenderer: toolTipRenderer, headerName: 'Key', flex: 2, minWidth: 120},
    {field: 'file_name', cellRenderer: toolTipRenderer, headerName: 'File Name', flex: 3, minWidth: 150},
    {field: 'mime_type', cellRenderer: toolTipRenderer, headerName: 'Type', flex: 2, minWidth: 120},
    {
      field: 'file_size', headerName: 'Size', flex: 1, minWidth: 100,
      valueFormatter: params => formatFileSize(params.value),
    },
    {
      field: 'create_date', headerName: 'Uploaded', flex: 2, minWidth: 150,
      valueFormatter: params => params.value ? dateTimeToStr(new Date(params.value)) : '',
    },
    {
      field: 'used_on_pages', headerName: 'Used On', flex: 1, minWidth: 100,
      headerTooltip: `The number of ${pageLabel}s whose content has this key`,
      valueGetter: params => (params.data.used_on_pages || []).length,
      cellRenderer: params => <span title={(params.data.used_on_pages || []).join(', ')}>{params.value}</span>,
    },
    ...(canEdit ? [{
      headerName: 'Action',
      sortable: false, filter: false,
      maxWidth: 150, minWidth: 150,
      cellRenderer: params => {
        return <Fragment>
          <Button type="button" onClick={() => startEditingDocument(params.data)} size="sm" className="me-2">
            Edit
          </Button>
          <Button type="button" variant="danger" size="sm"
                  onClick={() => startDocumentAction(params.data, setDeletingDocument)}>
            Delete
          </Button>
        </Fragment>;
      }
    }] : []),
  ];

  const defaultColDefs = {
    sortable: true,
    filter: false,
    resizable: true,
  }

  // A course which has not been saved yet has no id, so there is nowhere to put a document
  if (!course?.id) {
    return (
      <Container fluid className="usacm-container-wide">
        <Row>
          <Col className="text-center mt-3">
            Save this {courseLabel.toLowerCase()} before adding documents.
          </Col>
        </Row>
        <Row>
          <Col className="usacm-button-row mt-3">
            <Button variant="secondary" onClick={() => onClose()}>Course List</Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="usacm-container-wide">

      <Row>
        <Col>
          <div className="ag-theme-alpine" style={{width: '100%', ...(hasFewRows ? {} : {height: '700px'})}}>
            <AgGridReact
              ref={gridRef}
              rowData={documentList}
              columnDefs={colDefs}
              defaultColDef={defaultColDefs}
              gridOptions={{enableCellTextSelection: true, ensureDomOrder: true}} // enable cell selection
              domLayout={hasFewRows ? "autoHeight" : "normal"}
              getRowId={(params) => params.data.id} // prevent re-rendering the whole table when data changes
            />
          </div>
        </Col>
      </Row>

      {getErrorMessageForField(errors, '') &&
        <Row>
          <Col className="text-center mt-3 usacm-error-message">
            {getErrorMessageForField(errors, '')}
          </Col>
        </Row>
      }

      <Row>
        <Col className="usacm-button-row mt-3">
          <Button variant="secondary" onClick={() => onClose()}>Course List</Button>
          {canEdit &&
            <Button type="button" onClick={() => openDocumentDialog({})}>Add Document</Button>
          }
        </Col>
      </Row>

      <Modal show={!!editingDocument}
             onHide={() => setEditingDocument(null)}
             size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isAdding ? 'Add Document' : 'Edit Document'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          <Row className="mb-3">
            <Col className="col-7">
              <Form.Group controlId="edit_document_title">
                <FloatingLabel controlId="edit_document_title" label="Title">
                  <Form.Control type="text"
                                placeholder="Title"
                                name="title"
                                value={editTitle}
                                onChange={e => setEditTitle(e.target.value)}
                                isInvalid={fieldHasErrors(editErrors, 'title')}/>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(editErrors, 'title')}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col className="col-5">
              <Form.Group controlId="edit_document_doc_key">
                <FloatingLabel controlId="edit_document_doc_key" label="Key">
                  <Form.Control type="text"
                                placeholder="Key"
                                name="doc_key"
                                value={editDocKey}
                                onChange={e => setEditDocKey(e.target.value.toLowerCase())}
                                isInvalid={fieldHasErrors(editErrors, 'doc_key')}/>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(editErrors, 'doc_key')}
                  </Form.Control.Feedback>
                </FloatingLabel>
                <Form.Text muted>Unique in this {courseLabel.toLowerCase()}. Letters, numbers, _ and -.</Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {editKeyChanged && renderKeyInUseWarning(editingDocument, 'If you change the key')}

          <Row className="mt-3">
            <Col>
              <Form.Group controlId="edit_document_file">
                {isAdding ?
                  <Form.Label>File</Form.Label>
                  :
                  <Form.Label>Replace the file <span className="text-muted">
                    (currently {editingDocument?.file_name}, {formatFileSize(editingDocument?.file_size)})
                  </span></Form.Label>
                }
                <Form.Control type="file"
                              name="file"
                              onChange={e => setEditFile(e.target.files[0] || null)}
                              isInvalid={fieldHasErrors(editErrors, 'file')}/>
                <Form.Control.Feedback type="invalid">
                  {getErrorMessageForField(editErrors, 'file')}
                </Form.Control.Feedback>
                <Form.Text muted>
                  {isAdding ? '' : 'Optional - leave empty to keep the current file. '}Maximum {maxMb}MB.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {getErrorMessageForField(editErrors, '') &&
            <Row>
              <Col className="text-center mt-3 usacm-error-message">
                {getErrorMessageForField(editErrors, '')}
              </Col>
            </Row>
          }
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditingDocument(null)}>Cancel</Button>
          <Button variant="primary"
                  disabled={!editTitle.trim() || !editDocKey.trim() || (isAdding && !editFile)}
                  onClick={() => callSaveDocument()}>
            {isAdding ? 'Upload Document' : 'Update Document'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!deletingDocument}
             onHide={() => setDeletingDocument(null)}
             size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete "{deletingDocument?.title}"?
            This deletes the document and its file for good.
          </p>
          {renderKeyInUseWarning(deletingDocument, 'If you delete it')}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeletingDocument(null)}>Cancel</Button>
          <Button variant="danger" onClick={() => callDeleteDocument()}>Delete Document</Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
}
